<template>
  <div
    :class="[
      'swiper-container',
      $style.slider,
      'w-full',
      'flex-1',
      `swiper-container-${_uid}`
    ]"
  >
    <div class="swiper-wrapper">
      <div
        class="swiper-slide"
        v-for="(item, index) in slides"
        :key="item[keyName]"
      >
        <slot name="slide" :item="item" :index="index" />
        <div
          v-if="sliderOptions.lazy"
          class="swiper-lazy-preloader swiper-lazy-preloader-white"
        />
      </div>
    </div>

    <div
      v-if="total && showPagination"
      :class="[
        sliderOptions.pagination.type === 'bullets' && $style.bullets,
        sliderOptions.pagination.type === 'fraction' && $style.fraction,
        aside && $style.centerAside
      ]"
    >{{swiper && swiper.activeIndex + 1}} {{$t('components.common.b-image-slider.of')}} {{ total }}</div>

    <div
      v-else-if="showPagination"
      :class="[
        'swiper-pagination',
        sliderOptions.pagination.type === 'bullets' && $style.bullets,
        sliderOptions.pagination.type === 'fraction' && $style.fraction,
        aside && $style.centerAside
      ]"
    />

    <div
      v-show="isNavVisible"
      data-testid="previous-button"
      :class="[
        'swiper-button-prev',
        'swiper-button-white',
        showNavButtonsBackground && 'swiper-button-background',
      ]"
    >
      <b-svg name="chevron-left" :size="24"/>
    </div>
    <div
      v-show="isNavVisible"
      data-testid="next-button"
      :class="[
        'swiper-button-next',
        'swiper-button-white',
        showNavButtonsBackground && 'swiper-button-background',
      ]"
    >
      <b-svg name="chevron-right" :size="24"/>
    </div>
  </div>
</template>

<script>
import { useDeviceDetect } from '@/composables/device-detect'

export default {

  emits: [
    'slide-change',
  ],
  name: 'b-slider',

  setup () {
    const { touchable } = useDeviceDetect()

    return {
      touchable,
    }
  },

  data () {
    return {
      swiper: null,
      defaultOptions: {
        observer: true,
        observeParents: true,
        keyboard: {
          enabled: true,
        },
        spaceBetween: 24,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          dynamicBullets: true,
          clickable: true,
        },
        on: {
          slideChange: function () {
            this.$emit('slide-change', this.swiper.activeIndex)
          }.bind(this),
        },
      },
    }
  },

  props: {
    options: Object,
    slides: {
      type: Array,
      required: true,
    },
    keyName: {
      type: String,
      default: 'id',
    },
    aside: {
      type: Boolean,
      default: false,
    },
    manualInit: Boolean,
    autoplay: Boolean,
    total: Number,
    showNavButtonsBackground: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    sliderOptions () {
      if (this.autoplay) {
        return {
          ...this.defaultOptions,
          autoplay: {
            delay: 4000,
            disableOnInteraction: false,
          },
          ...this.options,
        }
      }
      return { ...this.defaultOptions, ...this.options }
    },
    isNavVisible () {
      if (!this.sliderOptions.navigation) return false
      let isVisible = !this.touchable
      if (this.swiper && this.swiper.slides.length === 1) isVisible = false
      return isVisible
    },
  },

  methods: {
    update () {
      this.swiper.update()
      this.swiper.lazy.load()
    },
    init (index = 0) {
      // lazy import (this component is used occasionally)
      import('swiper').then(module => {
        this.$nextTick(() => {
          const Swiper = module.default
          this.swiper = new Swiper(this.$el, this.sliderOptions)
          this.slideTo(index)
        })
      })
    },
    slideTo (index, speed = 0, runCallbacks = false) {
      this.swiper.slideTo(index, speed, runCallbacks)
    },
    slideNext () {
      this.swiper.slideNext()
    },
  },

  watch: {
    slides () {
      if (this.swiper) {
        this.$nextTick(() => {
          this.update()
        })
      }
    },
  },

  created () {
    if (!this.manualInit) {
      this.init(0)
    }
  },
}
</script>

<style lang="scss" module>
@layer legacy-views {

.slider {
  :global {
    .swiper-slide {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    .swiper-pagination-bullet {
      background: white;
      transform: scale(.83)
    }
    .swiper-button-next,
    .swiper-button-prev {
      width: 4rem;
      height: 4rem;
      border-radius: $border-radius;

      &:after {
        font-size: 20px;
      }
    }
    .swiper-button-background {
      background-color: $color-primary;
    }
  }
}
.fraction {
  position: fixed;
  color: var(--color-white);
  @include size('28-b');
  top: 27px;
  height: 48px;
  width: auto;
  margin: 0 auto;
  left: 50%;
  transform: translate(-50%, 0);
}

.centerAside {
  left: calc((100% - 320px) * 0.5)
}

}</style>
